import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const API_BASE =
  import.meta.env?.VITE_API_URL ||
  (process.env.NODE_ENV === "development" ? "http://localhost:3001" : "");

function getHeaders(token: string | null): HeadersInit {
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  token: string | null
): Promise<T> {
  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      ...getHeaders(token),
      ...options.headers,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Request failed" }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }

  return res.json();
}

export const api = {
  // Auth
  login: (username: string, password: string, token: string | null) =>
    request<{ token: string; user: { id: number; username: string; role: string } }>(
      "/api/login",
      { method: "POST", body: JSON.stringify({ username, password }) },
      token
    ),

  getMe: (token: string | null) =>
    request<{ id: number; username: string; role: string; created_at: string; last_login: string | null }>(
      "/api/me",
      {},
      token
    ),

  // Status
  getStatus: (token: string | null) =>
    request<{ hbbs: string; hbbr: string; key: string; timestamp: string }>("/api/status", {}, token),

  // Devices
  getDevices: (token: string | null) =>
    request<{ devices: any[]; count: number; groups: any[] }>("/api/devices", {}, token),

  updateDevice: (id: string, data: any, token: string | null) =>
    request<{ success: boolean }>(`/api/devices/${id}`, { method: "PUT", body: JSON.stringify(data) }, token),

  deleteDevice: (id: string, token: string | null) =>
    request<{ success: boolean }>(`/api/devices/${id}`, { method: "DELETE" }, token),

  // Groups
  getGroups: (token: string | null) =>
    request<any[]>("/api/groups", {}, token),

  createGroup: (data: { name: string; description?: string; policyId?: number }, token: string | null) =>
    request<{ success: boolean; id: number }>("/api/groups", { method: "POST", body: JSON.stringify(data) }, token),

  updateGroup: (id: number, data: { name: string; description?: string; policyId?: number }, token: string | null) =>
    request<{ success: boolean }>(`/api/groups/${id}`, { method: "PUT", body: JSON.stringify(data) }, token),

  deleteGroup: (id: number, token: string | null) =>
    request<{ success: boolean }>(`/api/groups/${id}`, { method: "DELETE" }, token),

  // Policies
  getPolicies: (token: string | null) =>
    request<any[]>("/api/policies", {}, token),

  createPolicy: (data: { name: string; description?: string; config: any }, token: string | null) =>
    request<{ success: boolean; id: number }>("/api/policies", { method: "POST", body: JSON.stringify(data) }, token),

  updatePolicy: (id: number, data: { name: string; description?: string; config: any }, token: string | null) =>
    request<{ success: boolean }>(`/api/policies/${id}`, { method: "PUT", body: JSON.stringify(data) }, token),

  deletePolicy: (id: number, token: string | null) =>
    request<{ success: boolean }>(`/api/policies/${id}`, { method: "DELETE" }, token),

  // Audit Logs
  getAuditLogs: (params: { limit?: number; offset?: number; type?: string; deviceId?: string; severity?: string; startDate?: string; endDate?: string; token: string | null }) => {
    const { token, ...queryParams } = params;
    const searchParams = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) searchParams.set(key, String(value));
    });
    return request<{ logs: any[]; total: number }>(`/api/audit-logs?${searchParams.toString()}`, {}, token);
  },

  getConnectionHistory: (deviceId: string, limit: number, token: string | null) =>
    request<any[]>(`/api/connection-history?deviceId=${deviceId}&limit=${limit}`, {}, token),

  // API Tokens
  getApiTokens: (token: string | null) =>
    request<any[]>("/api/tokens", {}, token),

  createApiToken: (data: { name: string; permissions: string[]; expiresInDays?: number }, token: string | null) =>
    request<{ success: boolean; id: number; token: string }>("/api/tokens", { method: "POST", body: JSON.stringify(data) }, token),

  deleteApiToken: (id: number, token: string | null) =>
    request<{ success: boolean }>(`/api/tokens/${id}`, { method: "DELETE" }, token),

  // Client Configs
  getClientConfigs: (token: string | null) =>
    request<any[]>("/api/client-configs", {}, token),

  createClientConfig: (data: { name: string; config: any; isDefault?: boolean }, token: string | null) =>
    request<{ success: boolean; id: number }>("/api/client-configs", { method: "POST", body: JSON.stringify(data) }, token),

  updateClientConfig: (id: number, data: { name: string; config: any; isDefault?: boolean }, token: string | null) =>
    request<{ success: boolean }>(`/api/client-configs/${id}`, { method: "PUT", body: JSON.stringify(data) }, token),

  deleteClientConfig: (id: number, token: string | null) =>
    request<{ success: boolean }>(`/api/client-configs/${id}`, { method: "DELETE" }, token),

  exportClientConfig: (id: number, token: string | null) =>
    request<any>(`/api/client-configs/${id}/export`, {}, token),

  // System
  restartServices: (token: string | null) =>
    request<{ success: boolean }>("/api/restart", { method: "POST" }, token),

  getLogs: (lines: number, token: string | null) =>
    request<{ logs: string }>(`/api/logs?lines=${lines}`, {}, token),

  generateKey: (token: string | null) =>
    request<{ success: boolean; newKey: string; backup: string }>("/api/key/generate", { method: "POST" }, token),

  // Users
  getUsers: (token: string | null) =>
    request<any[]>("/api/users", {}, token),

  createUser: (data: { username: string; password: string; role: string }, token: string | null) =>
    request<{ success: boolean; id: number }>("/api/users", { method: "POST", body: JSON.stringify(data) }, token),

  updateUser: (id: number, data: { username?: string; password?: string; role?: string }, token: string | null) =>
    request<{ success: boolean }>(`/api/users/${id}`, { method: "PUT", body: JSON.stringify(data) }, token),

  deleteUser: (id: number, token: string | null) =>
    request<{ success: boolean }>(`/api/users/${id}`, { method: "DELETE" }, token),

  // Address Book
  getAddressBook: (token: string | null) =>
    request<any[]>("/api/address-book", {}, token),

  createAddressBookEntry: (data: { peer_id: string; ip: string; name: string; group_name?: string; password?: string; notes?: string; tags?: string }, token: string | null) =>
    request<{ success: boolean; id: number }>("/api/address-book", { method: "POST", body: JSON.stringify(data) }, token),

  updateAddressBookEntry: (id: number, data: { peer_id: string; ip: string; name: string; group_name?: string; password?: string; notes?: string; tags?: string }, token: string | null) =>
    request<{ success: boolean }>(`/api/address-book/${id}`, { method: "PUT", body: JSON.stringify(data) }, token),

  deleteAddressBookEntry: (id: number, token: string | null) =>
    request<{ success: boolean }>(`/api/address-book/${id}`, { method: "DELETE" }, token),

  bulkAddToAddressBook: (deviceIds: string[], token: string | null) =>
    request<{ success: boolean; added: number }>("/api/address-book/bulk-add", { method: "POST", body: JSON.stringify({ deviceIds }) }, token),
};